import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import signableDocsService from '../services/signableDocsService';

// Public typed-name signing page for a Manual eSign document. No auth — the
// unguessable token in the URL is the only credential. Mirrors the lease-sign
// experience: review the PDF, type your name, affirm intent + consent, submit.
const PublicSignDoc = () => {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [name, setName] = useState('');
  const [intent, setIntent] = useState(false);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await signableDocsService.publicMeta(token);
        setMeta(data);
        if (data.status === 'signed') setSigned(true);
        const pdf = await signableDocsService.publicPdf(token);
        setPdfUrl(URL.createObjectURL(pdf.data));
      } catch (e) {
        setError(e.response?.data?.detail || 'This signing link is invalid or has expired.');
      }
    })();
  }, [token]);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await signableDocsService.publicSubmit(token, {
        signature_name: name, intent_acknowledged: intent, consent_acknowledged: consent,
      });
      setSigned(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not submit your signature.');
    } finally { setBusy(false); }
  };

  if (error && !meta) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500 text-[14px]">{error}</div>;
  }
  if (!meta) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;

  if (signed) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <CheckCircle className="w-14 h-14 text-emerald-500 mb-4" />
        <h1 className="text-xl font-semibold text-slate-800">Signature received</h1>
        <p className="text-[14px] text-slate-500 mt-2">
          Thank you. Your electronically signed copy of the {meta.title} has been recorded.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-xl font-semibold text-slate-800">{meta.title}</h1>
        <p className="text-[13px] text-slate-500 mb-4">{meta.facility_name}</p>

        {pdfUrl && <iframe title="document" src={pdfUrl} className="w-full h-[520px] rounded-lg border border-slate-200 bg-white mb-5" />}

        {error && <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-[13px]">{error}</div>}

        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <label className="block text-[12px] font-medium text-slate-600 mb-1">Type your full name to sign</label>
          <input value={name} onChange={(e) => setName(e.target.value)}
            className="w-full text-[14px] border border-slate-200 rounded-lg px-3 py-2 mb-4"
            placeholder="Your full legal name" />
          <label className="flex items-start gap-2 text-[13px] text-slate-600 mb-2">
            <input type="checkbox" checked={intent} onChange={(e) => setIntent(e.target.checked)} className="mt-0.5" />
            <span>{meta.intent_statement}</span>
          </label>
          <label className="flex items-start gap-2 text-[13px] text-slate-600 mb-4">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
            <span>{meta.consent_statement}</span>
          </label>
          <button onClick={submit} disabled={busy || !name || !intent || !consent}
            className="w-full text-[14px] px-4 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            {busy ? 'Submitting…' : 'Sign document'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublicSignDoc;
